/* ============================================================
   BLOODLINE FW26 — core behaviour (preview theme only)
   All globals namespaced under window.BLOODLINE.

   Modules:
   - chrome()  : measures REAL height of bottom-fixed chrome (cookie banner,
                 Shopify preview bar) + mobile keyboard, exposes it as the CSS
                 custom property --bl-chrome-bottom so no BLOODLINE UI is ever
                 covered. Reacts to resize/mutation, not a guessed padding.
   - reveal()  : scroll reveal with real failsafes (bfcache, resize, no-IO,
                 far-below-fold, reduced motion) — content is NEVER left hidden.
   - popup()   : early-access popup, session-once, focus trapped.
   - forms()   : inline early-access form helper + consent-safe analytics.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var docEl = document.documentElement;
  docEl.classList.remove('no-js');
  docEl.classList.add('bl-js');

  /* ==========================================================
     1. CHROME OFFSET — the cookie banner problem, solved properly
     ========================================================== */
  // Selectors for bottom-anchored overlays we must never sit under.
  // Shopify native privacy banner + Consentmo variants + Shopify preview bar.
  var CHROME_SELECTORS = [
    '#shopify-pc__banner',
    '.shopify-pc__banner__dialog',
    '#PBarNextFrameWrapper',
    '[id*="consentmo" i][class*="banner" i]',
    '.consentmo-banner',
    '#consentmo_banner',
    '.gdpr-cookie-banner',
    '#cookie-consent-banner'
  ];

  var chromeState = { bottom: 0, raf: 0 };

  function measureChrome() {
    var vh = window.innerHeight || docEl.clientHeight;
    var maxOverlap = 0;

    for (var i = 0; i < CHROME_SELECTORS.length; i++) {
      var nodes;
      try { nodes = document.querySelectorAll(CHROME_SELECTORS[i]); } catch (e) { continue; }
      for (var j = 0; j < nodes.length; j++) {
        var el = nodes[j];
        if (!el || !el.getBoundingClientRect) continue;
        var cs = window.getComputedStyle(el);
        if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') continue;
        if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
        var r = el.getBoundingClientRect();
        if (r.height < 8 || r.width < 40) continue;
        // only count overlays anchored to the BOTTOM half of the viewport
        if (r.bottom < vh - 4) continue;
        var overlap = Math.max(0, vh - r.top);
        if (overlap > maxOverlap && overlap < vh * 0.75) maxOverlap = overlap;
      }
    }

    // mobile keyboard: visualViewport shrinks while the page height does not
    var kb = 0;
    if (window.visualViewport) {
      kb = Math.max(0, vh - window.visualViewport.height - window.visualViewport.offsetTop);
      if (kb < 60) kb = 0; // ignore URL-bar jitter
    }

    var next = Math.round(Math.max(maxOverlap, kb));
    if (next !== chromeState.bottom) {
      chromeState.bottom = next;
      docEl.style.setProperty('--bl-chrome-bottom', next + 'px');
      docEl.classList.toggle('bl-chrome-active', next > 0);
      docEl.classList.toggle('bl-keyboard-open', kb > 0);
    }
  }

  function scheduleMeasure() {
    if (chromeState.raf) return;
    chromeState.raf = window.requestAnimationFrame(function () {
      chromeState.raf = 0;
      measureChrome();
    });
  }

  function initChrome() {
    docEl.style.setProperty('--bl-chrome-bottom', '0px');
    measureChrome();

    // React to the banner appearing/disappearing/resizing — not a fixed guess.
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(scheduleMeasure);
      CHROME_SELECTORS.forEach(function (sel) {
        try {
          document.querySelectorAll(sel).forEach(function (el) { ro.observe(el); });
        } catch (e) {}
      });
      // re-observe when the app injects its markup later
      var mo = new MutationObserver(function () {
        CHROME_SELECTORS.forEach(function (sel) {
          try {
            document.querySelectorAll(sel).forEach(function (el) { try { ro.observe(el); } catch (e) {} });
          } catch (e) {}
        });
        scheduleMeasure();
      });
      mo.observe(document.body, { childList: true, subtree: true });
    } else {
      window.setInterval(scheduleMeasure, 800);
    }

    window.addEventListener('resize', scheduleMeasure, { passive: true });
    window.addEventListener('orientationchange', scheduleMeasure, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleMeasure, { passive: true });
      window.visualViewport.addEventListener('scroll', scheduleMeasure, { passive: true });
    }
    // banner dismissal usually happens on click somewhere in it
    document.addEventListener('click', function () { window.setTimeout(measureChrome, 60); }, true);
    // a few late passes for slow third-party injection
    [400, 1200, 2500, 5000].forEach(function (t) { window.setTimeout(measureChrome, t); });
  }

  /* ==========================================================
     2. REVEAL — never leaves content invisible
     ========================================================== */
  var io = null;

  function revealAll(root) {
    (root || document).querySelectorAll('.bl-reveal').forEach(function (el) { el.classList.add('is-in'); });
  }

  function initReveal(root) {
    var els = (root || document).querySelectorAll('.bl-reveal:not(.is-in)');
    if (!els.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      revealAll(root);
      return;
    }

    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px 20% 0px', threshold: 0.01 });
    }
    els.forEach(function (el) { io.observe(el); });

    // Failsafe: a rAF-driven sweep that remembers the deepest point ever reached.
    // A fast flick-scroll coalesces scroll events and can outrun an IO; this
    // cannot, because it re-evaluates on every frame while the page moves and
    // never "un-reveals" what the reader has already scrolled past.
    startSweep(root);
  }

  // Depth-memory sweep. Driven by scroll/resize events plus a low-frequency
  // interval backstop — deliberately NOT requestAnimationFrame, which browsers
  // throttle in background tabs and headless runs. Never un-reveals.
  var sweepState = { maxDepth: 0, timer: 0, scope: null };
  function sweepNow() {
    var scope = sweepState.scope || document;
    var vh = window.innerHeight;
    var depth = (window.scrollY || window.pageYOffset || 0) + vh;
    if (depth > sweepState.maxDepth) sweepState.maxDepth = depth;
    var limit = sweepState.maxDepth + vh * 1.5;
    var pending = scope.querySelectorAll('.bl-reveal:not(.is-in)');
    for (var i = 0; i < pending.length; i++) {
      var el = pending[i];
      if (el.getBoundingClientRect().top + window.scrollY < limit) {
        el.classList.add('is-in');
        if (io) io.unobserve(el);
      }
    }
    if (!pending.length && sweepState.timer) {
      window.clearInterval(sweepState.timer);
      sweepState.timer = 0;
    }
  }
  function startSweep(root) {
    sweepState.scope = root || document;
    sweepNow();
    if (!sweepState.timer) sweepState.timer = window.setInterval(sweepNow, 200);
    window.addEventListener('scroll', sweepNow, { passive: true });
    window.addEventListener('resize', sweepNow, { passive: true });
    window.addEventListener('orientationchange', sweepNow, { passive: true });
    // The document grows as images and fonts land. Without this the first pass
    // measures a short page and everything below stays hidden — the exact cause
    // of "reveals never triggered" in full-page captures.
    if (window.ResizeObserver) {
      try { new ResizeObserver(sweepNow).observe(document.body); } catch (e) {}
    }
    window.addEventListener('load', sweepNow);
  }

  // back/forward cache + reduced-motion toggled at runtime
  window.addEventListener('pageshow', function (e) { if (e.persisted) { revealAll(); measureChrome(); } });
  if (reduceMotion.addEventListener) {
    reduceMotion.addEventListener('change', function () { if (reduceMotion.matches) revealAll(); });
  }

  /* ==========================================================
     3. ANALYTICS — consent-aware, never any PII
     ========================================================== */
  var SAFE_KEYS = ['method', 'mode', 'result_tier', 'form_version', 'source', 'placement', 'has_marketing_consent'];
  function track(eventName, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: eventName };
      if (params) {
        Object.keys(params).forEach(function (k) {
          if (SAFE_KEYS.indexOf(k) !== -1) payload[k] = params[k];
        });
      }
      window.dataLayer.push(payload);
      if (window.BLOODLINE_DEBUG) console.info('[bl:track]', payload);
    } catch (e) { /* analytics must never break UX */ }
  }

  /* ==========================================================
     4. EARLY-ACCESS POPUP
     ========================================================== */
  function initPopup() {
    var popup = document.querySelector('[data-bl-popup]');
    if (!popup) return;
    if (document.body.getAttribute('data-bl-template') === 'blacksheep') return;

    var KEY = 'bl_ea_popup_' + (popup.getAttribute('data-version') || '1');
    var DONE = 'bl_ea_done';

    // A 0 non si apre da sola: resta solo su richiesta. E' il modo giusto,
    // perche' aprendosi a tempo finiva sopra al modulo della pagina e ne
    // mangiava il click: nessuno riusciva a iscriversi.
    var delayMs = parseInt(popup.getAttribute('data-delay'), 10) || 8000;
    var scrollPct = parseInt(popup.getAttribute('data-scroll'), 10) || 35;
    var opened = false, lastFocus = null, timer = 0;

    function markSeen() { try { sessionStorage.setItem(KEY, '1'); } catch (e) {} }
    function inlineFormBusy() {
      // never cover the inline form while the visitor is filling it in
      var a = document.activeElement;
      if (a && a.closest && a.closest('[data-bl-region="early-access"]')) return true;
      var inline = document.querySelector('[data-bl-region="early-access"]');
      if (inline) {
        var r = inline.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.9 && r.bottom > 0) return true;
      }
      return false;
    }
    function open(forced) {
      if (opened) return;
      if (!forced) {
        // Questi rinvii valgono solo per l'apertura automatica: se l'ha
        // chiesta la persona, si apre e basta.
        var room = (window.innerHeight || 0) - (chromeState.bottom || 0);
        if (room < 380) { timer = window.setTimeout(open, 3000); return; }
        if (inlineFormBusy()) { timer = window.setTimeout(open, 4000); return; }
        markSeen();
      }
      opened = true;
      cleanup();
      lastFocus = document.activeElement;
      popup.hidden = false;
      window.requestAnimationFrame(function () { popup.classList.add('is-open'); });
      var f = popup.querySelector('input, button, [href]');
      if (f) f.focus();
      document.addEventListener('keydown', onKey);
      track('early_access_shown', { source: 'popup' });
    }
    function close() {
      opened = false;
      popup.classList.remove('is-open');
      document.removeEventListener('keydown', onKey);
      window.setTimeout(function () { popup.hidden = true; }, 300);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var f = popup.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    function onScroll() {
      var h = docEl;
      var pct = (h.scrollTop || document.body.scrollTop) / ((h.scrollHeight - h.clientHeight) || 1) * 100;
      if (pct >= scrollPct) open(false);
    }
    function cleanup() {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    }
    // Su richiesta: qualunque elemento con data-bl-access-open lo apre.
    // Delegato sul documento, cosi' vale anche per quello che arriva dopo.
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[data-bl-access-open]') : null;
      if (!t) return;
      e.preventDefault();
      track('early_access_open', { source: t.getAttribute('data-bl-access-open') || 'cta' });
      open(true);
    });

    // Apertura automatica solo se qualcuno l'ha configurata, e una volta sola.
    var auto = popup.getAttribute('data-auto') === 'true';
    try {
      if (sessionStorage.getItem(KEY)) auto = false;
      if (localStorage.getItem(DONE)) auto = false;
    } catch (e) { /* storage bloccato: si lascia com'e' */ }
    if (auto) {
      timer = window.setTimeout(function () { open(false); }, delayMs || 8000);
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    popup.querySelectorAll('[data-bl-popup-close]').forEach(function (b) { b.addEventListener('click', close); });
    popup.addEventListener('click', function (e) { if (e.target === popup) close(); });
    popup.addEventListener('submit', function () { try { localStorage.setItem(DONE, '1'); } catch (e) {} });
  }

  /* ==========================================================
     5. EARLY-ACCESS INLINE FORMS
     ========================================================== */
  function initForms() {
    document.querySelectorAll('form[data-bl-earlyaccess]').forEach(function (form) {
      var status = form.querySelector('[data-bl-status]');
      var version = form.getAttribute('data-form-version') || '1';
      form.addEventListener('submit', function () {
        var consent = form.querySelector('[data-bl-consent]');
        track('early_access_submit', {
          source: form.getAttribute('data-source') || 'inline',
          placement: form.getAttribute('data-placement') || 'inline',
          form_version: version,
          has_marketing_consent: consent ? (consent.checked ? 'yes' : 'no') : 'n/a'
        });
        try { localStorage.setItem('bl_ea_done', '1'); } catch (e) {}
        var email = form.querySelector('input[type="email"]');
        if (window.klaviyo && email && email.value) {
          try {
            var id = { $email: email.value.trim().toLowerCase(), bloodline_source: 'bloodline_early_access' };
            if (typeof window.klaviyo.identify === 'function') window.klaviyo.identify(id);
            else window.klaviyo.push(['identify', id]);
          } catch (e) {}
        }
        if (status) status.textContent = '';
      });
      if (/customer_posted=true/.test(window.location.search) && status) {
        status.hidden = false;
        status.setAttribute('data-state', 'ok');
        status.textContent = form.getAttribute('data-success') || 'YOU’RE IN. CHECK YOUR INBOX.';
        try { localStorage.setItem('bl_ea_done', '1'); } catch (e) {}
      }
    });
  }

  /* ==========================================================
     boot
     ========================================================== */
  /* <details data-bl-accordion="mobile"> is authored open so the content is
     never hidden from desktop or from a crawler. Below 750px it collapses,
     which is the only place the brief allows an accordion. */
  function initAccordions() {
    var nodes = document.querySelectorAll('[data-bl-accordion="mobile"]');
    if (!nodes.length) return;
    var mq = window.matchMedia('(max-width: 749px)');
    function apply() {
      for (var i = 0; i < nodes.length; i++) {
        if (mq.matches) {
          if (!nodes[i].dataset.blTouched) nodes[i].open = false;
        } else {
          nodes[i].open = true;
        }
      }
    }
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].addEventListener('toggle', function () { this.dataset.blTouched = '1'; });
    }
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
  }

  /* ==========================================================
     accesso anticipato
     ========================================================== */
  var EA_KEY = 'bl_early_pass';   // rilasciato da blacksheep.js a giocata conclusa
  var BS_SEEN = 'bl_bs_seen';     // rilasciato all'apertura dell'attivazione

  function readLS(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function writeLS(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  function dropLS(k) { try { window.localStorage.removeItem(k); } catch (e) {} }

  var QS = (function () {
    try { return new URLSearchParams(window.location.search); } catch (e) { return { get: function () { return null; } }; }
  })();

  /* ?bl_reset=1 restituisce il visitatore allo stato di chi non e' mai
     passato di qui: serve a rivedere il pannello dopo averlo gia' aperto,
     altrimenti il lasciapassare resta e la password non si vede mai piu'. */
  function initPreviewReset() {
    if (QS.get('bl_reset') !== '1') return;
    dropLS(EA_KEY);
    dropLS(BS_SEEN);
  }

  function hasEarlyPass(unlockOn) {
    // 'complete' (predefinito): entra chi ha lasciato un contatto e tirato.
    // 'visit': basta essere passati dalla landing dell'attivazione.
    if (readLS(EA_KEY) === '1') return true;
    if (unlockOn === 'visit' && readLS(BS_SEEN) === '1') return true;
    return false;
  }

  function initEarlyGate() {
    var gate = document.querySelector('[data-bl-early-gate]');
    if (!gate) return;
    var unlockOn = gate.getAttribute('data-unlock-on') || 'complete';
    var expectedPw = (gate.getAttribute('data-pw') || '').trim().toLowerCase();

    /* Apertura in loco: il pannello si spegne, la riga compare, il sipario
       si alza e sotto c'e' gia' la pagina. Chi ha appena messo la password
       non va mandato altrove. */
    function openGate(source, instant) {
      writeLS(EA_KEY, '1');
      track('early_access_open', { source: source });
      if (instant) { gate.hidden = true; document.body.style.overflow = ''; return; }
      var ridotto = false;
      try { ridotto = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
      gate.classList.add('is-granted');
      window.setTimeout(function () { gate.classList.add('is-lifting'); }, ridotto ? 120 : 900);
      window.setTimeout(function () {
        gate.hidden = true;
        document.body.style.overflow = '';
      }, ridotto ? 260 : 1600);
    }

    if (hasEarlyPass(unlockOn)) { openGate('token', true); return; }

    // la chiave nell'indirizzo: chi arriva dal bottone della mail non digita
    var chiave = (QS.get('k') || '').trim().toLowerCase();
    if (chiave && expectedPw && chiave === expectedPw) { openGate('link', false); return; }

    gate.hidden = false;

    var form = gate.querySelector('[data-bl-early-form]');
    var input = gate.querySelector('#bl-eg-pw');
    var err = gate.querySelector('[data-bl-early-err]');
    var expected = (gate.getAttribute('data-pw') || '').trim().toLowerCase();

    if (input) { try { input.focus({ preventScroll: true }); } catch (e) {} }

    // Il fuoco resta dentro il pannello finché è aperto: senza questo si
    // tabula nel negozio che sta dietro, che è proprio quello che blocchiamo.
    gate.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var f = gate.querySelectorAll('a[href], button, input:not([type="hidden"])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      var given = (input.value || '').trim().toLowerCase();
      if (!given) { showErr(err && err.getAttribute('data-empty')); return; }
      if (given !== expected) {
        showErr(null);
        track('early_access_denied', { source: 'password' });
        return;
      }
      openGate('password', false);
    });

    function showErr(msg) {
      if (!err) return;
      err.hidden = false;
      err.textContent = msg || err.getAttribute('data-invalid') || '';
      if (input) { input.value = ''; try { input.focus(); } catch (e) {} }
    }
  }

  /* La pagina dell'accesso anticipato mostra uno dei tre stati in base alla
     fase del lancio e al lasciapassare. Nessuno stato e' visibile finche' il
     JS non decide: meglio un attimo di vuoto che mostrare la password a chi
     e' gia' dentro, o il conto alla rovescia a finestra gia' aperta. */
  function initEarlyPage() {
    var root = document.querySelector('[data-bl-early-page]');
    if (!root) return;
    var unlockOn = root.getAttribute('data-unlock-on') || 'complete';
    var phase = (window.BLOODLINE_MODE && window.BLOODLINE_MODE.phase) || 'PRE';
    var states = root.querySelectorAll('[data-state]');

    function show(name) {
      for (var i = 0; i < states.length; i++) {
        states[i].hidden = states[i].getAttribute('data-state') !== name;
      }
    }

    var expected = (root.getAttribute('data-pw') || '').trim().toLowerCase();
    var shopUrl = root.getAttribute('data-shop-url') || '/';

    /* Dal lancio pubblico questo indirizzo non e' piu' una porta: il link
       girera' per settimane, e chi ci arriva tardi non deve trovare una
       schermata su una finestra chiusa. Va dritto alla collezione. */
    if (phase === 'PUBLIC' && !QS.get('bl_state')) {
      window.location.replace(shopUrl);
      return;
    }

    /* La chiave nell'indirizzo: chi arriva dal bottone della mail non digita
       niente. E' il passaggio dove si perde piu' gente, su una tastiera
       mobile e in maiuscolo. */
    function welcomeThenEnter(source) {
      writeLS(EA_KEY, '1');
      track('early_access_open', { source: source });
      var w = root.querySelector('[data-state="welcome"]');
      if (!w) { window.location.href = shopUrl; return; }
      show('welcome');
      var hold = Number(root.getAttribute('data-welcome-ms') || 2200);
      // Chi ha chiesto meno movimento non aspetta l'animazione.
      try {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) hold = 600;
      } catch (e) {}
      root.setAttribute('data-welcome-playing', 'true');
      window.setTimeout(function () { window.location.href = shopUrl; }, hold);
      var skip = root.querySelector('[data-bl-welcome-skip]');
      if (skip) skip.addEventListener('click', function () { window.location.href = shopUrl; });
    }

    // ?bl_state= scavalca la scelta: e' l'unico modo di vedere gli stati
    // uno dopo l'altro senza spostare le date o svuotare il browser.
    var forcedState = QS.get('bl_state');
    if (forcedState === 'soon' || forcedState === 'locked' || forcedState === 'open' || forcedState === 'welcome') {
      show(forcedState);
      if (forcedState !== 'locked') return;
    } else {
      var key = (QS.get('k') || '').trim().toLowerCase();
      if (key && expected && key === expected) { welcomeThenEnter('link'); return; }
      if (hasEarlyPass(unlockOn)) { show('open'); return; }
      if (phase === 'PRE') { show('soon'); return; }
      show('locked');
    }

    var form = root.querySelector('[data-bl-early-form]');
    var input = root.querySelector('#bl-eap-pw');
    var err = root.querySelector('[data-bl-early-err]');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var given = (input.value || '').trim().toLowerCase();
      if (!given) { fail(err && err.getAttribute('data-empty')); return; }
      if (given !== expected) { fail(null); track('early_access_denied', { source: 'page' }); return; }
      welcomeThenEnter('password');
    });

    function fail(msg) {
      if (!err) return;
      err.hidden = false;
      err.textContent = msg || err.getAttribute('data-invalid') || '';
      if (input) { input.value = ''; try { input.focus(); } catch (e) {} }
    }
  }

  /* Chi ha vinto un codice alla serata se lo porta dietro: il gioco lo salva
     e qui lo si rimette davanti, perche' nessuno torna indietro a cercarlo
     nella cronologia mentre sta scegliendo una taglia. */
  function initCodePromemoria() {
    var M = window.BLOODLINE_MODE || {};
    if (M.phase === 'PUBLIC') return;
    var rec = null;
    try {
      var raw = null, ls = window.localStorage;
      for (var i = 0; i < ls.length; i++) {
        var k = ls.key(i);
        if (k && k.indexOf('bl_bs_') === 0 && k.indexOf('_v') > 0) { raw = ls.getItem(k); break; }
      }
      if (raw) rec = JSON.parse(raw);
    } catch (e) { return; }
    if (!rec || !rec.code) return;

    // Chiusa una volta, resta chiusa: ricordare un codice non vale la pena
    // di coprire la pagina a ogni schermata.
    var CHIUSA = 'bl_codebar_off';
    if (readLS(CHIUSA) === '1') return;

    var bar = document.createElement('div');
    bar.className = 'bl-codebar';
    bar.setAttribute('role', 'status');
    bar.innerHTML =
      '<button type="button" class="bl-codebar__code" data-bl-copy-code ' +
      'title="' + (rec.prizeLabel ? String(rec.prizeLabel) : '') + ' — tocca per copiare">' +
      String(rec.code) + '</button>' +
      '<button type="button" class="bl-codebar__x" data-bl-codebar-close aria-label="Nascondi il codice">×</button>';
    document.body.appendChild(bar);

    bar.querySelector('[data-bl-codebar-close]').addEventListener('click', function () {
      writeLS(CHIUSA, '1');
      bar.remove();
    });

    var btn = bar.querySelector('[data-bl-copy-code]');
    btn.addEventListener('click', function () {
      var testo = String(rec.code);
      function fatto() {
        btn.classList.add('is-copied');
        var prima = btn.textContent;
        btn.textContent = 'COPIATO';
        window.setTimeout(function () { btn.textContent = prima; btn.classList.remove('is-copied'); }, 1400);
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(testo).then(fatto, function () {});
      } else {
        var t = document.createElement('textarea');
        t.value = testo; document.body.appendChild(t); t.select();
        try { document.execCommand('copy'); fatto(); } catch (e) {}
        document.body.removeChild(t);
      }
      track('code_copied', { source: 'codebar' });
    });
  }

  /* ==========================================================
     il fit intero: scegli le taglie, aggiungi tutto in un gesto
     ========================================================== */
  function initFullFit() {
    var root = document.querySelector('[data-bl-fullfit]');
    if (!root) return;
    var righe = Array.prototype.slice.call(root.querySelectorAll('[data-bl-fit-row]'));
    if (!righe.length) return;

    var soglia = parseFloat(root.getAttribute('data-soglia') || '0') * 100;  // in centesimi
    var totEl = root.querySelector('[data-bl-fit-total]');
    var shipEl = root.querySelector('[data-bl-fit-ship]');
    var allBtn = root.querySelector('[data-bl-fit-add-all]');
    var allLbl = root.querySelector('[data-bl-fit-all-label]');
    var errEl = root.querySelector('[data-bl-fit-err]');
    var etichettaBase = allLbl ? allLbl.textContent : '';
    var aggiuntaInCorso = false;

    function soldi(cent) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'it', {
          style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR',
        }).format(cent / 100);
      } catch (e) { return '€' + (cent / 100).toFixed(2); }
    }

    function scelti() {
      var out = [];
      righe.forEach(function (r) {
        if (r.getAttribute('data-aggiunto') === '1') return;
        var attivo = r.querySelector('[data-bl-fit-size].is-on');
        if (attivo) out.push({ riga: r, id: attivo.getAttribute('data-variant'),
          prezzo: parseInt(r.getAttribute('data-prezzo'), 10) || 0 });
      });
      return out;
    }

    function dipingi() {
      var sel = scelti();
      var tot = sel.reduce(function (a, x) { return a + x.prezzo; }, 0);
      if (totEl) totEl.textContent = soldi(tot);
      if (allBtn) allBtn.disabled = aggiuntaInCorso || sel.length === 0;
      if (allLbl) {
        allLbl.textContent = sel.length > 1
          ? etichettaBase + ' — ' + sel.length + ' PEZZI'
          : etichettaBase;
      }
      // Quanto manca alla spedizione gratuita: si dice qui, dove c'e' ancora
      // un pezzo da aggiungere, non nel carrello dove ormai si sta uscendo.
      if (shipEl && soglia > 0) {
        if (tot === 0) { shipEl.hidden = true; }
        else if (tot >= soglia) {
          shipEl.hidden = false; shipEl.classList.add('is-free');
          shipEl.textContent = txtFit('free', 'Spedizione gratuita in Italia inclusa.');
        } else {
          shipEl.hidden = false; shipEl.classList.remove('is-free');
          shipEl.textContent = txtFit('missing', 'Mancano %X% alla spedizione gratuita in Italia.')
            .replace('%X%', soldi(soglia - tot));
        }
      }
      righe.forEach(function (r) {
        var b = r.querySelector('[data-bl-fit-add-one]');
        if (!b) return;
        var ok = !!r.querySelector('[data-bl-fit-size].is-on');
        b.disabled = aggiuntaInCorso || !ok || r.getAttribute('data-aggiunto') === '1';
      });
    }
    function txtFit(k, d) { return d; }

    root.addEventListener('click', function (e) {
      if (aggiuntaInCorso) return;
      var sz = e.target.closest && e.target.closest('[data-bl-fit-size]');
      if (sz && sz.tagName === 'BUTTON' && !sz.disabled) {
        var riga = sz.closest('[data-bl-fit-row]');
        var eraAcceso = sz.classList.contains('is-on');
        riga.querySelectorAll('[data-bl-fit-size]').forEach(function (x) { x.classList.remove('is-on'); });
        // ricliccare la stessa toglie la scelta: serve a disfare un tocco sbagliato
        if (!eraAcceso) sz.classList.add('is-on');
        dipingi();
        return;
      }
      var uno = e.target.closest && e.target.closest('[data-bl-fit-add-one]');
      if (uno && !uno.disabled) {
        var r1 = uno.closest('[data-bl-fit-row]');
        var a = r1.querySelector('[data-bl-fit-size].is-on');
        if (a) aggiungi([{ id: a.getAttribute('data-variant'), quantity: 1 }], [r1]);
        return;
      }
      var tutti = e.target.closest && e.target.closest('[data-bl-fit-add-all]');
      if (tutti && !tutti.disabled) {
        var sel = scelti();
        aggiungi(sel.map(function (x) { return { id: x.id, quantity: 1 }; }), sel.map(function (x) { return x.riga; }));
      }
    });

    function aggiungi(items, righeCoinvolte) {
      if (!items.length || aggiuntaInCorso) return;
      aggiuntaInCorso = true;
      root.setAttribute('aria-busy', 'true');
      if (errEl) errEl.hidden = true;
      dipingi();
      var sezioni = [];
      document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
        var id = el.getAttribute('data-section-id');
        if (id && sezioni.indexOf(id) === -1) sezioni.push(id);
      });
      var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(base + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items, sections: sezioni.slice(0, 5), sections_url: window.location.pathname }),
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) {
          if (!res.ok || res.j.status) throw new Error((res.j && res.j.description) || 'errore');
          righeCoinvolte.forEach(function (r) {
            r.setAttribute('data-aggiunto', '1');
            var b = r.querySelector('[data-bl-fit-add-one]');
            if (b) { b.classList.add('is-done'); b.textContent = '✓'; b.disabled = true; }
          });
          track('fullfit_add', { count: items.length });
          // A failed cart read must not turn a successful add into a retryable failure.
          return fetch(base + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
            .then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); })
            .catch(function () { return null; })
            .then(function (cart) {
              var completo = cart && typeof cart.item_count === 'number';
              // Match events.js without importing modules into this CDN-compiled classic script.
              document.dispatchEvent(new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: completo ? cart : undefined,
                  sourceId: 'bloodline-fullfit',
                  data: {
                    source: completo ? 'bloodline-fullfit' : 'product-form-component',
                    itemCount: completo ? cart.item_count : items.reduce(function (n, item) { return n + item.quantity; }, 0),
                    sections: res.j.sections,
                  },
                },
              }));
            });
        })
        .catch(function (err) {
          if (errEl) { errEl.hidden = false; errEl.textContent = String(err.message || err); }
        })
        .finally(function () {
          aggiuntaInCorso = false;
          root.removeAttribute('aria-busy');
          dipingi();
        });
    }

    dipingi();
  }

  /* ==========================================================
     carrello: quanto manca alla spedizione gratuita
     ========================================================== */
  function initCartShipping() {
    var M = window.BLOODLINE_MODE || {};
    var cfg = M.shipping;
    if (!cfg || !cfg.free) return;

    // La soglia e' quella del paese di chi guarda: mostrarne una sola vorrebbe
    // dire promettere a un tedesco una cosa che vale solo in Italia.
    function zona() {
      var c = String(cfg.country || 'IT').toUpperCase();
      if (c === 'IT') return { k: 'IT', soglia: cfg.free.IT };
      if ((cfg.eu || []).indexOf(c) >= 0) return { k: 'EU', soglia: cfg.free.EU };
      return { k: 'WORLD', soglia: cfg.free.WORLD };
    }
    var z = zona();
    if (!(z.soglia > 0)) return;
    var sogliaCent = z.soglia * 100;

    function soldi(cent) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'it', {
          style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR',
        }).format(cent / 100);
      } catch (e) { return '€' + (cent / 100).toFixed(2); }
    }

    function costruisci() {
      var el = document.createElement('div');
      el.className = 'bl-ship';
      el.setAttribute('data-bl-ship', '');
      el.innerHTML =
        '<p class="bl-ship__line" data-bl-ship-line></p>' +
        '<div class="bl-ship__track" aria-hidden="true"><span class="bl-ship__fill" data-bl-ship-fill></span></div>';
      return el;
    }

    function dipingi(totale) {
      var barre = document.querySelectorAll('[data-bl-ship]');
      for (var i = 0; i < barre.length; i++) {
        var bar = barre[i];
        var line = bar.querySelector('[data-bl-ship-line]');
        var fill = bar.querySelector('[data-bl-ship-fill]');
        var pct = Math.max(0, Math.min(100, (totale / sogliaCent) * 100));
        if (fill) fill.style.width = pct + '%';
        bar.classList.toggle('is-free', totale >= sogliaCent);
        if (!line) continue;
        var dove = z.k === 'IT' ? 'in Italia' : (z.k === 'EU' ? 'in Europa' : '');
        if (totale >= sogliaCent) {
          line.textContent = 'Spedizione gratuita ' + dove + ' inclusa.';
        } else {
          line.textContent = 'Mancano ' + soldi(sogliaCent - totale) +
            ' alla spedizione gratuita ' + dove + '.';
        }
      }
    }

    function inserisci() {
      var punti = document.querySelectorAll('.cart-totals');
      for (var i = 0; i < punti.length; i++) {
        var p = punti[i];
        if (p.previousElementSibling && p.previousElementSibling.hasAttribute &&
            p.previousElementSibling.hasAttribute('data-bl-ship')) continue;
        p.parentNode.insertBefore(costruisci(), p);
      }
    }

    function aggiorna() {
      inserisci();
      if (!document.querySelector('[data-bl-ship]')) return;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var vuoto = !c || !c.item_count;
          document.querySelectorAll('[data-bl-ship]').forEach(function (b) { b.hidden = vuoto; });
          if (!vuoto) dipingi(c.items_subtotal_price != null ? c.items_subtotal_price : c.total_price);
        })
        .catch(function () {});
    }

    aggiorna();
    // il carrello cambia da piu' punti: cassetto, pagina, pulsanti quantita'
    ['cart:update', 'cart:refresh', 'cart:add'].forEach(function (ev) {
      document.addEventListener(ev, function () { window.setTimeout(aggiorna, 220); });
    });
    // il cassetto si apre dopo, e il suo contenuto arriva con lui
    var mo = new MutationObserver(function () {
      if (document.querySelector('.cart-totals:not([data-bl-ship-done])')) {
        document.querySelectorAll('.cart-totals').forEach(function (x) { x.setAttribute('data-bl-ship-done', '1'); });
        aggiorna();
      }
    });
    try { mo.observe(document.body, { childList: true, subtree: true }); } catch (e) {}
  }

  function initPhaseFlag() {
    var M = window.BLOODLINE_MODE || {};
    if (!M.forcedPhase || M.forcedPhase === 'auto') return;
    var el = document.createElement('div');
    el.className = 'bl-phase-flag';
    el.innerHTML = '<span>ANTEPRIMA — fase forzata: ' + String(M.phase) + '</span>';
    document.body.appendChild(el);
  }

  function boot() {
    // La landing di campagna (/pages/bloodline) nasconde header e footer del
    // negozio. La homepage li tiene: e' la porta d'ingresso e deve portare
    // navigazione, ricerca, account e carrello.
    // Il tipo di pagina conta: le schede prodotto FW26 usano lo stesso
    // suffisso 'bloodline', e senza questo controllo restavano senza header.
    if (document.body.getAttribute('data-bl-template') === 'bloodline' &&
        document.body.getAttribute('data-bl-page-type') === 'page') {
      document.body.classList.add('bl-campaign-page');
    }
    // Blacksheep and /21 are full-bleed experiences: no store header, footer or
    // menus, whichever route they are served from.
    if (document.querySelector('[data-bl-blacksheep]') || document.querySelector('[data-bl-region="private-21"]')) {
      document.body.classList.add('bl-bare-page');
    }
    initChrome();
    initPhaseFlag();
    initFullFit();
    initCartShipping();
    initCodePromemoria();
    initPreviewReset();
    initEarlyGate();
    initEarlyPage();
    initAccordions();
    initReveal(document);
    initPopup();
    initForms();
    if (document.body.getAttribute('data-bl-template') === 'bloodline') {
      track('bloodline_landing_view', { mode: 'preview' });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.BLOODLINE = {
    track: track,
    initReveal: initReveal,
    revealAll: revealAll,
    measureChrome: measureChrome,
    chromeBottom: function () { return chromeState.bottom; },
    sweepState: sweepState,
    sweepNow: sweepNow,
    reduceMotion: reduceMotion.matches
  };
  document.addEventListener('shopify:section:load', function (e) { initReveal(e.target); measureChrome(); });
})();
